import React, { useEffect, useRef, useState } from "react";
import usmApi from "@/lib/usmApi";
import { PageHeader } from "./UsmLayout";
import HubTabs from "./hub/HubTabs";
import Can from "@/components/usm/Can";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Copy, Download, Upload } from "lucide-react";
import LabelDesigner from "./labels/LabelDesigner";
import { defaultElementsFor, PRESETS, blankProfileFromPreset } from "./labels/labelHelpers";

/**
 * Label Templates list page.
 *
 * Lists all saved label profiles and lets settings.edit users:
 *   • Create new templates from jewelry-tag presets (Ring / Necklace / etc.)
 *   • Edit/Designer existing templates
 *   • Duplicate
 *   • Delete
 *   • Import JSON (drop a previously-exported template)
 *
 * The actual visual designer lives in <LabelDesigner /> — opening this modal
 * presents the full BarTender-style editor with drag-to-position, font
 * controls, and live PNG preview.
 */
export default function LabelTemplates() {
  const { hasPerm } = useUsmAuth();
  const canEdit = hasPerm("settings.edit");
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { mode, data }
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await usmApi.get("/labels/profiles");
      setProfiles(r.data || []);
    } catch (e) { toast.error("Şablonlar yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`'${p.name}' şablonu silinsin mi?`)) return;
    try {
      await usmApi.delete(`/labels/profiles/${p.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silme başarısız"); }
  };

  const duplicate = async (p) => {
    try {
      const r = await usmApi.post(`/labels/profiles/${p.id}/duplicate`);
      toast.success(`'${r.data.name}' oluşturuldu`);
      load();
    } catch (e) { toast.error("Kopyalama başarısız"); }
  };

  const exportTemplate = async (p) => {
    try {
      const r = await usmApi.get(`/labels/profiles/${p.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(p.name || "label").replace(/\s+/g, "_")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Şablon indirildi (JSON)");
    } catch (e) { toast.error("Dışa aktarma başarısız"); }
  };

  const handleImport = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const r = await usmApi.post("/labels/profiles/import", data);
      toast.success(`'${r.data.name}' içe aktarıldı`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "İçe aktarma başarısız"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const newFromPreset = (preset) => {
    setEditing({ mode: "create", data: blankProfileFromPreset(preset, profiles.length === 0) });
  };

  const openDesigner = (p) => {
    // Make sure elements is populated client-side too (defensive in case the
    // server ever returns an old v1 profile)
    const data = { ...p, elements: p.elements || defaultElementsFor(p.width_mm, p.height_mm, p.margin_mm, p.show_fields) };
    setEditing({ mode: "edit", data });
  };

  return (
    <div data-testid="usm-label-templates">
      <HubTabs section="branches" />
      <PageHeader
        title="Etiket Şablonları"
        breadcrumbs={[{ label: "Panel", to: "/usm" }, { label: "Etiket Şablonları" }]}
        actions={canEdit && (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="application/json" onChange={(e) => handleImport(e.target.files?.[0])} className="hidden" data-testid="tpl-import-file" />
            <button data-testid="tpl-import-btn" onClick={() => fileRef.current?.click()}
              className="text-xs uppercase tracking-[0.2em] px-5 py-3 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2">
              <Upload size={14} /> İçe Aktar
            </button>
            <PresetMenu onPick={newFromPreset} />
          </div>
        )}
      />

      <div className="bg-white border border-[#E7E5E4] overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-[#FAFAF9] text-[11px] uppercase tracking-[0.15em] text-[#71717A]">
            <tr>
              <th className="text-left px-4 py-3">Şablon</th>
              <th className="text-left px-4 py-3">Boyut</th>
              <th className="text-left px-4 py-3">Barkod Tipi</th>
              <th className="text-left px-4 py-3">Görünür Alanlar</th>
              <th className="text-left px-4 py-3">Varsayılan</th>
              <th className="text-right px-4 py-3 w-44">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[#71717A]">Yükleniyor...</td></tr>
            ) : profiles.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#71717A]">Henüz şablon yok. Hazır bir şablon seçerek başlayın.</td></tr>
            ) : profiles.map((p) => {
              const visibleCount = Object.entries(p.elements || {}).filter(([, v]) => v?.visible).length;
              return (
                <tr key={p.id} data-testid={`tpl-row-${p.id}`} className="border-t border-[#F5F5F4]">
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-[#52525B] font-mono text-[12px]">{p.width_mm}×{p.height_mm} mm</td>
                  <td className="px-4 py-3 text-[#52525B] uppercase text-[11px] tracking-wider">{p.barcode_type}</td>
                  <td className="px-4 py-3 text-[#71717A] text-[12px]">{visibleCount} alan</td>
                  <td className="px-4 py-3">{p.is_default && <Star size={14} className="text-[#C8A96A]" fill="#C8A96A" />}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Can perm="settings.edit">
                      <button data-testid={`tpl-edit-${p.id}`} onClick={() => openDesigner(p)} className="p-2 hover:bg-[#FAFAF9]" title="Tasarımcıyı aç"><Pencil size={14} /></button>
                      <button data-testid={`tpl-dup-${p.id}`} onClick={() => duplicate(p)} className="p-2 hover:bg-[#FAFAF9]" title="Kopyala"><Copy size={14} /></button>
                      <button data-testid={`tpl-export-${p.id}`} onClick={() => exportTemplate(p)} className="p-2 hover:bg-[#FAFAF9]" title="Dışa Aktar (JSON)"><Download size={14} /></button>
                      <button data-testid={`tpl-del-${p.id}`} onClick={() => remove(p)} className="p-2 hover:bg-[#FAFAF9] text-red-600" title="Sil"><Trash2 size={14} /></button>
                    </Can>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <LabelDesigner
          mode={editing.mode}
          initial={editing.data}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PresetMenu({ onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const f = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", f);
    return () => document.removeEventListener("mousedown", f);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button data-testid="tpl-new-btn" onClick={() => setOpen((o) => !o)}
        className="bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] px-5 py-3 inline-flex items-center gap-2">
        <Plus size={14} /> Yeni Şablon
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 bg-white border border-[#E7E5E4] shadow-lg z-30 w-72">
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-[#71717A] border-b border-[#F5F5F4]">Hazır Şablonlar</div>
          {PRESETS.map((p) => (
            <button key={p.id} data-testid={`tpl-preset-${p.id}`} onClick={() => { setOpen(false); onPick(p); }}
              className="block w-full text-left px-3 py-2.5 hover:bg-[#FAFAF9] border-b border-[#F5F5F4] last:border-b-0">
              <div className="text-sm font-medium">{p.name}</div>
              <div className="text-[11px] text-[#71717A] font-mono">{p.width_mm}×{p.height_mm} mm</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
